'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { type FC, useEffect, useState } from 'react';
import { StorageImage } from '@/components/atoms/StorageImage';
import { WatchGoogleAdsFlow } from '@/entities/Actions/WatchGoogleAds';
import { useUserStore } from '@/entities/User/store/userStoreProvider';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import { Button } from '@/shared/components/Button';
import { useAirdropShare } from '@/shared/providers/AirdropShareProvider';
import { useCelebrityShare } from '@/shared/providers/CelebrityShareProvider';
import { useLeaderboardShare } from '@/shared/providers/LeaderboardShareProvider';
import type { ActionCardProps } from './ActionCard.types';
import { ActionProgress } from './ActionProgress';
import { ActionTag } from './ActionTag';
import { btnInfo } from './btnInfo';
import { InstallAppModal } from './InstallAppModal';
export const ActionCard: FC<ActionCardProps> = t0 => {
  const $ = _c(106);
  const {
    id,
    type,
    actionTypeName,
    title,
    celebrity,
    timeLeft,
    progressCurrent,
    progressTotal,
    tokenReward,
    gemReward,
    tokenCost,
    backgroundImage,
    buttonText,
    buttonIcon,
    href,
    onButtonClick,
    className: t1,
    activeQuestionIndex,
    countQuestions,
    auctionData
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t = useTranslations("experiences.card");
  const router = useRouter();
  const {
    isMobile
  } = useDeviceDetection();
  const user = useUserStore(_temp);
  const locale = useLocale();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [timeLeftData, setTimeLeft] = useState(t2);
  const [imageError, setImageError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdFlowActive, setIsAdFlowActive] = useState(false);
  const {
    openCelebrityShare
  } = useCelebrityShare();
  const {
    openAirdropShare
  } = useAirdropShare();
  const {
    openLeaderboardShare
  } = useLeaderboardShare();
  let t3;
  let t4;
  if ($[1] !== timeLeft) {
    t3 = () => {
      const calculateTimeLeft = () => {
        const difference = timeLeft ? timeLeft.getTime() - new Date().getTime() : 0;
        if (difference <= 0) {
          return {
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0
          };
        }
        return {
          days: Math.floor(difference / 86400000),
          hours: Math.floor(difference / 3600000 % 24),
          minutes: Math.floor(difference / 1000 / 60 % 60),
          seconds: Math.floor(difference / 1000 % 60)
        };
      };
      setTimeLeft(calculateTimeLeft());
      const timer = setInterval(() => {
        const newTimeLeft = calculateTimeLeft();
        setTimeLeft(newTimeLeft);
        if (newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
          clearInterval(timer);
        }
      }, 1000);
      return () => clearInterval(timer);
    };
    t4 = [timeLeft];
    $[1] = timeLeft;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setImageError(false);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== backgroundImage) {
    t6 = [backgroundImage];
    $[5] = backgroundImage;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  useEffect(t5, t6);
  const calcIcon = _temp2;
  let t7;
  if ($[7] !== router || $[8] !== user?.id) {
    t7 = actionType => {
      const requiresAuth = ["platform_watch_video", "platform_referral_purchase", "platform_referral_registration", "platform_install_app", "platform_gold_status_purchase", "platform_coins_purchase", "platform_share_celebrity", "platform_share_airdrop", "platform_share_leaderboard"];
      if (requiresAuth.includes(actionType) && !user?.id) {
        router.push("/auth");
        return false;
      }
      return true;
    };
    $[7] = router;
    $[8] = user?.id;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const checkAuthForPlatformAction = t7;
  const isClickable = actionTypeName !== "platform_install_app" && actionTypeName !== "platform_watch_video" && actionTypeName !== "platform_referral_purchase" && actionTypeName !== "platform_referral_registration" && actionTypeName !== "platform_coins_purchase" && actionTypeName !== "platform_share_ee" && actionTypeName !== "platform_share_celebrity" && actionTypeName !== "platform_share_airdrop" && actionTypeName !== "platform_share_leaderboard";
  let t8;
  if ($[10] !== id || $[11] !== isClickable || $[12] !== locale || $[13] !== router || $[14] !== title || $[15] !== type) {
    t8 = () => {
      if (isClickable) {
        router.push(`/${locale}/earn/${type}/${encodeURIComponent(title.replace(/ /g, "-"))}-${id}`);
      }
    };
    $[10] = id;
    $[11] = isClickable;
    $[12] = locale;
    $[13] = router;
    $[14] = title;
    $[15] = type;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleCardClick = t8;
  let t9;
  if ($[17] !== actionTypeName || $[18] !== auctionData || $[19] !== celebrity || $[20] !== checkAuthForPlatformAction || $[21] !== href || $[22] !== locale || $[23] !== onButtonClick || $[24] !== openAirdropShare || $[25] !== openCelebrityShare || $[26] !== openLeaderboardShare || $[27] !== router) {
    t9 = e => {
      if (href) {
        e.preventDefault();
      }
      if (actionTypeName && !checkAuthForPlatformAction(actionTypeName)) {
        return;
      }
      if (actionTypeName === "platform_share_ee" && auctionData) {
        router.push(`/${locale}/auctions/${auctionData.eeId}-${encodeURIComponent(auctionData.eeTitle.replace(/ /g, "-"))}?share=true`);
      }
      if (actionTypeName === "platform_coins_purchase") {
        router.push(`/${locale}/settings/my-wallet/buy-coins`);
      } else {
        if (actionTypeName === "platform_install_app") {
          setIsModalOpen(true);
        } else {
          if (actionTypeName === "platform_watch_video") {
            setIsAdFlowActive(true);
          } else {
            if (actionTypeName === "platform_referral_purchase" || actionTypeName === "platform_referral_registration") {
              router.push("/settings/referral");
            } else {
              if (actionTypeName === "platform_share_celebrity" && celebrity?.id) {
                openCelebrityShare(celebrity.id, celebrity);
              } else {
                if (actionTypeName === "platform_share_airdrop") {
                  openAirdropShare();
                } else {
                  if (actionTypeName === "platform_share_leaderboard") {
                    openLeaderboardShare();
                  } else {
                    onButtonClick?.();
                  }
                }
              }
            }
          }
        }
      }
    };
    $[17] = actionTypeName;
    $[18] = auctionData;
    $[19] = celebrity;
    $[20] = checkAuthForPlatformAction;
    $[21] = href;
    $[22] = locale;
    $[23] = onButtonClick;
    $[24] = openAirdropShare;
    $[25] = openCelebrityShare;
    $[26] = openLeaderboardShare;
    $[27] = router;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const handleButtonClick = t9;
  let t10;
  if ($[29] !== celebrity) {
    t10 = celebrity != null ? `${celebrity.firstName ?? ""} ${celebrity.lastName ?? ""}`.trim() : "";
    $[29] = celebrity;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const celebrityLine = t10;
  const t11 = `flex h-full w-full flex-col items-center justify-between cursor-pointer ${className}`;
  const t12 = isClickable ? handleCardClick : handleButtonClick;
  let t13;
  if ($[31] !== actionTypeName || $[32] !== backgroundImage || $[33] !== celebrity || $[34] !== imageError || $[35] !== title) {
    t13 = actionTypeName !== "sm_growth_celebrity" && actionTypeName !== "sm_growth_like" && actionTypeName !== "sm_growth_comment" && actionTypeName !== "sm_growth_share" && actionTypeName !== "sm_growth_follow" && actionTypeName !== "sm_growth_subscribe" && actionTypeName !== "sm_growth_view" && actionTypeName !== "sm_growth_listen" && actionTypeName !== "sm_growth_watch" ? <div className="absolute inset-0 z-0">{backgroundImage && !imageError ? <StorageImage src={backgroundImage} alt={title} className="h-full w-full object-cover" withBlur={true} sources={[{
        media: "(min-width: 1280px)",
        width: 397,
        height: 586,
        resize: "cover"
      }, {
        media: "(min-width: 768px)",
        width: 343,
        height: 506,
        resize: "cover"
      }, {
        media: "(min-width: 0px)",
        width: 343,
        height: 506,
        resize: "cover"
      }]} /> : <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#1a1a2e] to-[#2a2a3e]"><div className="p-8 text-center"><div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#3a3a4e]"><svg className="h-8 w-8 text-[#e9ecf0] opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div></div></div>}</div> : <div className="absolute inset-0 z-0 bg-[linear-gradient(180deg,#121262_0%,#1E1E2A_100%)] flex flex-col gap-5 justify-start items-center pt-[60px]"><div className="border-[10px] border-[#FFFFFF]/10 rounded-full w-[35%] md:w-[calc(100%/396*200)] overflow-hidden"><div className="pb-[100%] relative">{celebrity?.headerAvatarImg && <StorageImage src={celebrity?.headerAvatarImg} alt={title} className="absolute inset-0 h-full w-full object-cover" withBlur={true} sources={[{
            media: "(min-width: 1280px)",
            width: 397,
            height: 586,
            resize: "cover"
          }, {
            media: "(min-width: 768px)",
            width: 343,
            height: 506,
            resize: "cover"
          }, {
            media: "(min-width: 0px)",
            width: 343,
            height: 506,
            resize: "cover"
          }]} />}</div></div>{celebrity && <p className="font-[Montserrat] italic text-center text-[transparent] bg-clip-text bg-[linear-gradient(90deg,#FF4D00_0.26%,#FF8000_100.26%)] text-[30px] leading-[1] font-bold">{celebrity.firstName} <br />{celebrity.lastName}</p>}</div>;
    $[31] = actionTypeName;
    $[32] = backgroundImage;
    $[33] = celebrity;
    $[34] = imageError;
    $[35] = title;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t || $[38] !== timeLeftData.days || $[39] !== timeLeftData.hours || $[40] !== timeLeftData.minutes || $[41] !== timeLeftData.seconds) {
    t14 = timeLeftData.seconds > 0 ? <div className="flex items-center gap-2 rounded-[100px] bg-[#20202e] p-2"><svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M0.5 8.14844C0.5 3.73016 4.08172 0.148438 8.5 0.148438C12.9183 0.148438 16.5 3.73016 16.5 8.14844C16.5 12.5667 12.9183 16.1484 8.5 16.1484C4.08172 16.1484 0.5 12.5667 0.5 8.14844ZM10.0286 10.6198C10.2889 10.8802 10.7111 10.8802 10.9714 10.6198C11.2317 10.3595 11.2317 9.93737 10.9714 9.67704L9.16667 7.8723V4.8151C9.16667 4.44691 8.8682 4.14844 8.5 4.14844C8.1318 4.14844 7.83333 4.44691 7.83333 4.8151V8.14844C7.83333 8.32524 7.9036 8.49484 8.0286 8.61984L10.0286 10.6198Z" fill="#E9ECF0" /></svg><span className="font-['Inter'] text-[14px] leading-[1.3] text-[#e9ecf0]">{`${timeLeftData.days}${t("days")} ${timeLeftData.hours}${t("h")} ${timeLeftData.minutes}${t("min")} ${t("left")}`}</span></div> : <div />;
    $[37] = t;
    $[38] = timeLeftData.days;
    $[39] = timeLeftData.hours;
    $[40] = timeLeftData.minutes;
    $[41] = timeLeftData.seconds;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const t15 = actionTypeName === "platform_install_app" || actionTypeName === "platform_add_solana_address" || actionTypeName === "platform_register" || actionTypeName === "platform_ee_buy_it_now" || actionTypeName === "platform_ee_auction_bid" || actionTypeName === "platform_admin_coins_grant" || actionTypeName === "platform_referral_purchase" || actionTypeName === "platform_competition_first_visit" || actionTypeName === "platform_referral_registration" || actionTypeName === "platform_coins_purchase" || actionTypeName === "platform_gold_status_purchase" || actionTypeName === "platform_bind_social_media" || actionTypeName === "platform_watch_video" || actionTypeName === "sm_growth_like" || actionTypeName === "sm_growth_comment" || actionTypeName === "sm_growth_share" || actionTypeName === "sm_growth_follow" || actionTypeName === "sm_growth_subscribe" || actionTypeName === "sm_growth_view" || actionTypeName === "sm_growth_listen" || actionTypeName === "sm_growth_watch" || actionTypeName === "sm_growth_celebrity" || actionTypeName === "platform_share_ee" || actionTypeName === "platform_share_celebrity" || actionTypeName === "platform_share_airdrop" || actionTypeName === "platform_share_leaderboard" ? actionTypeName : type as 'all' | 'poll' | 'quiz' | 'challenge' | 'platform';
  let t16;
  if ($[43] !== t15) {
    t16 = <ActionTag type={t15} />;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t14 || $[46] !== t16) {
    t17 = <div className="z-1 relative flex w-full items-center justify-between p-4 gap-2">{t14}{t16}</div>;
    $[45] = t14;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== actionTypeName || $[49] !== activeQuestionIndex || $[50] !== celebrity || $[51] !== celebrityLine || $[52] !== countQuestions || $[53] !== progressCurrent || $[54] !== progressTotal || $[55] !== title || $[56] !== type) {
    t18 = actionTypeName !== "sm_growth_celebrity" && <div className="flex flex-col gap-2">{celebrity && celebrityLine ? <div className="flex items-start gap-2.5"><span className="font-['Inter'] text-[13px] leading-[18px] text-[rgba(255,255,255,0.7)]">{celebrityLine}</span>{celebrity.isValidated && <Image src="/icons/verified-blue.svg" alt="Verified" className="size-5" width={18} height={18} />}</div> : null}<h3 className="line-clamp-2 truncate whitespace-normal font-['Inter'] text-[22px] font-medium leading-[1.15] text-[#e9ecf0]">{title}</h3>{typeof activeQuestionIndex !== "undefined" && <div className="relative flex h-[30px] w-full overflow-hidden rounded-full bg-[#000000]"><div className="absolute w-full px-4 py-2 text-sm leading-none text-white">{activeQuestionIndex}/{countQuestions} questions answered</div><div className="self-stretch bg-[#1A54D6] transition-all" style={{
          width: countQuestions ? `${activeQuestionIndex / countQuestions * 100}%` : 0
        }} /></div>}{progressCurrent !== undefined && progressTotal !== undefined && <ActionProgress current={progressCurrent} total={progressTotal} type={type} />}</div>;
    $[48] = actionTypeName;
    $[49] = activeQuestionIndex;
    $[50] = celebrity;
    $[51] = celebrityLine;
    $[52] = countQuestions;
    $[53] = progressCurrent;
    $[54] = progressTotal;
    $[55] = title;
    $[56] = type;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== actionTypeName || $[59] !== gemReward || $[60] !== t || $[61] !== tokenReward) {
    t19 = (!!tokenReward || !!gemReward) && (actionTypeName === "platform_share_celebrity" || actionTypeName === "platform_share_leaderboard") ? <div className="flex flex-row items-center justify-between gap-2 w-full"><span className="font-['Inter'] text-[14px] leading-[16px] text-[rgba(255,255,255,0.7)]">{actionTypeName === "platform_share_leaderboard" ? t("share_and_get_reward") : t("share_and_get_reward_celebrity")}</span><div className="flex gap-2">{!!gemReward && <div className="flex items-center justify-center gap-1 rounded-[100px] bg-[#e9ecf0] px-3 py-2"><div className="h-[18px] w-[18px]"><Image src="/images/actions/coin.png" alt="Gems" width={18} height={18} /></div><span className="font-['Inter'] text-[16px] font-semibold leading-[1.3] text-black">{gemReward}</span></div>}{!!tokenReward && <div className="flex items-center justify-center gap-1 rounded-[100px] bg-[#e9ecf0] px-3 py-2"><div className="h-[18px] w-[18px]"><Image className="h-full w-full" src="/images/actions/gem.png" alt="Tokens" width={18} height={18} /></div><span className="font-['Inter'] text-[16px] font-semibold leading-[1.3] text-black">{tokenReward}</span></div>}</div></div> : <div className="flex flex-col gap-2"><span className="font-['Inter'] text-[14px] leading-[16px] text-[rgba(255,255,255,0.7)]">{t("reward")}</span><div className="flex gap-2">{!!gemReward && <div className="flex items-center justify-center gap-1 rounded-[100px] bg-[#e9ecf0] px-3 py-2"><div className="h-[18px] w-[18px]"><Image src="/images/actions/coin.png" alt="Gems" width={18} height={18} /></div><span className="font-['Inter'] text-[16px] font-semibold leading-[1.3] text-black">{gemReward}</span></div>}{!!tokenReward && <div className="flex items-center justify-center gap-1 rounded-[100px] bg-[#e9ecf0] px-3 py-2"><div className="h-[18px] w-[18px]"><Image className="h-full w-full" src="/images/actions/gem.png" alt="Tokens" width={18} height={18} /></div><span className="font-['Inter'] text-[16px] font-semibold leading-[1.3] text-black">{tokenReward}</span></div>}</div></div>;
    $[58] = actionTypeName;
    $[59] = gemReward;
    $[60] = t;
    $[61] = tokenReward;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== t || $[64] !== tokenCost) {
    t20 = !!tokenCost && <div className="flex flex-col items-end gap-2"><span className="font-['Inter'] text-[14px] leading-[16px] text-[rgba(255,255,255,0.7)]">{t("cost")}</span><div className="flex w-[70px] items-center justify-center gap-1 rounded-[100px] bg-[#4B4B4B] px-3 py-2"><div className="relative h-[18px] w-[18px]"><Image src="/images/actions/coin.png" alt="Tokens" width={18} height={18} /></div><span className="font-['Inter'] text-[16px] font-semibold leading-[1.3] text-[#e9ecf0]">{tokenCost}</span></div></div>;
    $[63] = t;
    $[64] = tokenCost;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  let t21;
  if ($[66] !== t19 || $[67] !== t20) {
    t21 = <div className="flex items-end justify-between">{t19}{t20}</div>;
    $[66] = t19;
    $[67] = t20;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  let t22;
  if ($[69] !== actionTypeName || $[70] !== buttonIcon || $[71] !== buttonText || $[72] !== type) {
    t22 = buttonIcon ? <div className="h-[28px] w-[28px]"><Image src={buttonIcon} alt={buttonText} width={28} height={28} /></div> : calcIcon(actionTypeName || type);
    $[69] = actionTypeName;
    $[70] = buttonIcon;
    $[71] = buttonText;
    $[72] = type;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  const t23 = actionTypeName || type;
  let t24;
  if ($[74] !== t || $[75] !== t23) {
    t24 = btnInfo(t23, t);
    $[74] = t;
    $[75] = t23;
    $[76] = t24;
  } else {
    t24 = $[76];
  }
  let t25;
  if ($[77] !== t22 || $[78] !== t24) {
    t25 = <Button textClassName="text-[16px] font-bold  leading-[1.2] !justify-start" variant="whiteSecondary" size="md_a" width="full" rightIcon={t22}>{t24}</Button>;
    $[77] = t22;
    $[78] = t24;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] !== t18 || $[81] !== t21 || $[82] !== t25) {
    t26 = <div className="relative z-10 mt-auto w-full rounded-[14px] bg-[rgba(23,23,35,0.8)] p-4 backdrop-blur-md"><div className="flex flex-col gap-4">{t18}{t21}{t25}</div></div>;
    $[80] = t18;
    $[81] = t21;
    $[82] = t25;
    $[83] = t26;
  } else {
    t26 = $[83];
  }
  let t27;
  if ($[84] !== id || $[85] !== t11 || $[86] !== t12 || $[87] !== t13 || $[88] !== t17 || $[89] !== t26) {
    t27 = <div key={id} className={t11} onClick={t12}>{t13}{t17}{t26}</div>;
    $[84] = id;
    $[85] = t11;
    $[86] = t12;
    $[87] = t13;
    $[88] = t17;
    $[89] = t26;
    $[90] = t27;
  } else {
    t27 = $[90];
  }
  const cardContent = t27;
  const cardClassName = `relative flex flex-col items-center justify-between overflow-hidden rounded-[20px] h-[506px] md:h-[575px] transition-transform duration-200 hover:scale-[1.02] ${className}`;
  let t28;
  if ($[91] !== cardClassName || $[92] !== cardContent || $[93] !== href) {
    t28 = href ? <Link href={href} className={cardClassName}>{cardContent}</Link> : <div className={cardClassName}>{cardContent}</div>;
    $[91] = cardClassName;
    $[92] = cardContent;
    $[93] = href;
    $[94] = t28;
  } else {
    t28 = $[94];
  }
  let t29;
  if ($[95] !== actionTypeName || $[96] !== isMobile || $[97] !== isModalOpen) {
    t29 = actionTypeName === "platform_install_app" && <InstallAppModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} isMobile={isMobile} hasApp={true} />;
    $[95] = actionTypeName;
    $[96] = isMobile;
    $[97] = isModalOpen;
    $[98] = t29;
  } else {
    t29 = $[98];
  }
  let t30;
  if ($[99] !== actionTypeName || $[100] !== isAdFlowActive) {
    t30 = actionTypeName === "platform_watch_video" && <WatchGoogleAdsFlow isActive={isAdFlowActive} onClose={() => setIsAdFlowActive(false)} />;
    $[99] = actionTypeName;
    $[100] = isAdFlowActive;
    $[101] = t30;
  } else {
    t30 = $[101];
  }
  let t31;
  if ($[102] !== t28 || $[103] !== t29 || $[104] !== t30) {
    t31 = <>{t28}{t29}{t30}</>;
    $[102] = t28;
    $[103] = t29;
    $[104] = t30;
    $[105] = t31;
  } else {
    t31 = $[105];
  }
  return t31;
};
function _temp(store) {
  return store.user;
}
function _temp2(actionTypeName_0) {
  if (actionTypeName_0 === "platform_install_app") {
    return "download";
  }
  if (actionTypeName_0 === "platform_watch_video") {
    return "eye";
  }
  if (actionTypeName_0 === "quiz_participation") {
    return "quiz";
  }
  if (actionTypeName_0 === "challenge_participation") {
    return "arrowRightDote";
  }
  if (actionTypeName_0 === "poll_participation") {
    return "diagram";
  }
  if (actionTypeName_0 === "platform_referral_registration") {
    return "link";
  }
  if (actionTypeName_0 === "platform_share_ee" || actionTypeName_0 === "platform_share_celebrity" || actionTypeName_0 === "platform_share_airdrop" || actionTypeName_0 === "platform_share_leaderboard") {
    return "share";
  }
  if (actionTypeName_0 === "sm_growth_like" || actionTypeName_0 === "sm_growth_comment" || actionTypeName_0 === "sm_growth_share" || actionTypeName_0 === "sm_growth_follow" || actionTypeName_0 === "sm_growth_subscribe" || actionTypeName_0 === "sm_growth_view" || actionTypeName_0 === "sm_growth_listen" || actionTypeName_0 === "sm_growth_watch" || actionTypeName_0 === "sm_growth_celebrity") {
    return "sm_growth";
  }
  return "arrowRight";
}